import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MentionTextarea, MentionText } from "@/components/shared/Mentions";
import { Kbd } from "@/components/shared/ListNav";
import { STATUS_LABEL } from "@/components/TaskBadges";
import { autoPill } from "@/components/shared/Pill";

// 361: Aktivita úkolu = komentáře + historie v jednom proudu (dřív dvě karty). Nejnovější nahoře, pole pro
// komentář hned pod nadpisem (nový komentář se objeví pod ním), ukazuje se posledních 6, starší se rozbalí.
// Přepínač Vše / Komentáře. Odeslání tlačítkem nebo ⌘↵. 362: krátký placeholder — na telefonu se dlouhý zalomil a useknul.
const fmtTime = (iso) => new Intl.DateTimeFormat("cs-CZ", { timeZone: "Europe/Prague", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
const fmtDay = (iso) => new Intl.DateTimeFormat("cs-CZ", { timeZone: "Europe/Prague", day: "numeric", month: "numeric" }).format(new Date(iso));
const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
export function when(iso) {
  if (!iso) return "";
  const n = Math.round((dayStart(iso) - dayStart(new Date())) / 86400000);
  return n === 0 ? `dnes ${fmtTime(iso)}` : n === -1 ? `včera ${fmtTime(iso)}` : `${fmtDay(iso)} ${fmtTime(iso)}`;
}
const initials = (name) => ((name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()) || "?";

// 276: historie úkolu — kdo co kdy změnil
export const HIST_ACTION = {
  created: "Úkol zadán", updated: "Upraveno", submitted: "Odesláno ke schválení", completed: "Hotovo",
  approved: "Schváleno", unapproved: "Schválení zrušeno", returned: "Vráceno k přepracování", reopened: "Vráceno mezi rozpracované",
  check: "Odškrtnut bod", uncheck: "Zrušeno odškrtnutí bodu", photos_added: "Přidány fotky",
};
export function histChange(c) {
  if (c.field === "assignees") {
    return [c.added?.length ? `+ ${c.added.join(", ")}` : null, c.removed?.length ? `− ${c.removed.join(", ")}` : null]
      .filter(Boolean).join(" · ");
  }
  if (c.field === "status") return `${STATUS_LABEL[c.from] || c.from} → ${STATUS_LABEL[c.to] || c.to}`;
  return c.from !== undefined ? `${c.from} → ${c.to}` : c.to;
}
const DOT = { approved: "bg-success", completed: "bg-success", submitted: "bg-amber-500", returned: "bg-brand", unapproved: "bg-brand" };

export function TaskActivity({ task, user, createdByName, comment, setComment, picked, setPicked, onSend, busy, loadPeople }) {
  const [only, setOnly] = useState(false);   // jen komentáře
  const [all, setAll] = useState(false);
  const comments = task.comments || [];
  let hist = task.history || [];
  if (!hist.length && createdByName) hist = [{ id: "c", action: "created", user_name: createdByName, at: task.created_at }];
  const items = [...comments.map((c) => ({ k: "c", ...c })), ...(only ? [] : hist.map((h) => ({ k: "h", ...h })))]
    .sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const LIM = 6;
  const hidden = !all && items.length > LIM ? items.length - LIM : 0;
  const shown = hidden ? items.slice(0, LIM) : items;
  const nC = comments.length;
  const tog = (on) => `h-7 px-2.5 rounded-full text-xs font-semibold ${on ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`;
  return (
    <section className="pt-4 mt-4 border-t border-border/60" data-testid="task-activity">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-[15px] font-bold">Aktivita</h3>
        {nC > 0 && <span className="rounded-full bg-secondary text-muted-foreground px-2 py-0.5 text-xs font-semibold tabular-nums">{nC} {nC === 1 ? "komentář" : nC < 5 ? "komentáře" : "komentářů"}</span>}
        <div ref={autoPill} className="ml-auto flex p-0.5 rounded-full bg-secondary" role="group" aria-label="Co zobrazit">
          <button type="button" aria-pressed={!only} onClick={() => setOnly(false)} className={tog(!only)} data-testid="activity-all">Vše</button>
          <button type="button" aria-pressed={only} onClick={() => setOnly(true)} className={tog(only)} data-testid="activity-comments">Komentáře</button>
        </div>
      </div>
      <div className="flex items-start gap-2.5 mb-3"
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); onSend(); } }}>
        <span className="w-[30px] h-[30px] mt-1 shrink-0 rounded-full bg-brand/15 text-brand inline-flex items-center justify-center text-[11px] font-extrabold">{initials(user.name)}</span>
        <MentionTextarea value={comment} onChange={setComment} rows={1} loadPeople={loadPeople} picked={picked} onPick={setPicked}
          placeholder="Napsat komentář…" data-testid="comment-input"
          className="w-full min-w-0 min-h-[42px] rounded-[12px] resize-y" />
        <Button onClick={onSend} disabled={busy || !comment.trim()} data-testid="comment-send" className="h-[42px] rounded-full px-4 font-bold shrink-0" title="Odeslat (⌘↵)">
          {busy ? "Odesílám…" : "Odeslat"}
        </Button>
      </div>
      {shown.length === 0 && <p className="text-sm text-muted-foreground">Zatím žádné komentáře.</p>}
      <ol className="space-y-2.5">
        {shown.map((it) => (it.k === "c" ? (
          <li key={`c-${it.id}`} className="grid grid-cols-[30px_minmax(0,1fr)] gap-2.5 items-start" data-testid="comment-item">
            <span className="w-[30px] h-[30px] rounded-full bg-brand/15 text-brand inline-flex items-center justify-center text-[11px] font-extrabold">{initials(it.user_name)}</span>
            <div className="min-w-0 rounded-[12px] border border-border/70 bg-card px-3 py-2">
              <div className="flex items-baseline gap-2"><span className="text-sm font-bold">{it.user_name}</span><span className="text-xs text-muted-foreground tabular-nums">{when(it.at)}</span></div>
              <div className={`text-sm leading-[1.55] whitespace-pre-wrap break-words ${it.system ? "text-muted-foreground font-semibold" : ""}`}><MentionText text={it.text} mentions={it.mentions} /></div>
            </div>
          </li>
        ) : (
          <li key={`h-${it.id}`} className="grid grid-cols-[30px_minmax(0,1fr)] gap-2.5 items-start" data-testid="task-history-item">
            {/* puntík vlastní barvou — v tmavém režimu nesplývá s pozadím jako dřív barva linek */}
            <span className="h-5 grid place-items-center"><i className={`w-[7px] h-[7px] rounded-full ${DOT[it.action] || "bg-muted-foreground/45"}`} /></span>
            <div className="min-w-0 text-[13px] leading-snug text-muted-foreground">
              <b className="font-semibold text-foreground">{it.user_name}</b> · {HIST_ACTION[it.action] || it.action}{it.action === "photos_added" && it.note ? ` (${it.note})` : ""}
              {(it.changes || []).map((c) => <span key={c.field} className="break-words"> · {c.label}: <span className="tabular-nums">{histChange(c)}</span></span>)}
              {it.note && it.action !== "photos_added" && <span className="break-words"> · „{it.note}“</span>}
              {it.at && <span className="tabular-nums"> · {when(it.at)}</span>}
            </div>
          </li>
        )))}
      </ol>
      {hidden > 0 && (
        <button type="button" onClick={() => setAll(true)} data-testid="activity-more" className="mt-3 text-[13px] font-bold text-brand hover:underline">Zobrazit starší ({hidden})</button>
      )}
      <p className="hidden md:flex items-center gap-1 mt-3 text-[11.5px] text-muted-foreground"><Kbd>C</Kbd> komentář · <Kbd>⌘</Kbd><Kbd>↵</Kbd> odeslat · @ označí člověka</p>
    </section>
  );
}
